import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { ConfigurationSection, SpaceBetweenRow } from "@features/settings/pages/StyleComponents";
import { BodyLarge, BodyRegular, BodySmall } from "../../atoms/fonts/Body";
import Colours from "../../atoms/Colours";
import { Switch } from "../../atoms/Switch";
import { updateCompanySetting } from '@features/settings/services/CompanyService';
import { useDispatch, useSelector } from "react-redux";
import { selectCurrentUser, selectIsInTeams, updateCurrentUser } from '@shared/store/auth.duck';
import { failureNotification } from '@shared/store/notification.duck';
import { SimpleSpinner } from "../../atoms/SimpleSpinner";
import { useTranslation } from "react-i18next";
import { isPremiumTierUser } from '@shared/utils/AccessControl';
import { Column } from "../../atoms/StructuralLayout";

const NAME_DISPLAY_MODES = ['FULL_NAMES', 'INITIALS', 'RESERVED_ONLY'] as const;
type NameDisplayMode = typeof NAME_DISPLAY_MODES[number];

export function SignageSetting() {
  const [isLoading, setIsLoading] = useState(false);
  const dispatch = useDispatch();
  const user = useSelector(selectCurrentUser);
  const isInTeams = useSelector(selectIsInTeams);
  const { t } = useTranslation();

  const signageEnabled = user?.companyEntity?.signageEnabled;
  const nameDisplay = (user?.companyEntity?.signageNameDisplay || 'FULL_NAMES') as NameDisplayMode;
  const showSafetyRoles = user?.companyEntity?.signageShowSafetyRoles !== false;

  const isTierRestricted = !isPremiumTierUser(user);

  const save = async (field: 'SIGNAGE' | 'SIGNAGE_NAME_DISPLAY' | 'SIGNAGE_SHOW_SAFETY_ROLES', value: any) => {
    try {
      setIsLoading(true);
      await updateCompanySetting(field, value);
      dispatch(updateCurrentUser(true));
    } catch (e: any) {
      dispatch(failureNotification('Failed to update setting, please wait a minute.'));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <ConfigurationSection className={'domainBasedSignup__section'}>
      <SpaceBetweenRow>
        <Column>
          <BodyLarge colour={Colours.blue}>{t('settings.signage.title')}</BodyLarge>
          {isTierRestricted && (
            isInTeams
              ? <BodyRegular style={{marginTop: 4, color: Colours.darkGrey}}>
                  {t('settings.signage.not-enabled')}{' '}
                  <a href="mailto:support@team-today.com">support@team-today.com</a>
                </BodyRegular>
              : <BodyRegular style={{marginTop: 4, color: Colours.darkGrey}}>
                  {t('settings.signage.upsell')}{' '}
                  <Link to="/manage/payments">{t('settings.signage.upsell-link')}</Link>
                </BodyRegular>
          )}
        </Column>
        {isLoading ? <SimpleSpinner size={32} /> : <Switch value={!!signageEnabled} label=""
                                                           disabled={isLoading || isTierRestricted}
                                                           onChange={(value: boolean) => save('SIGNAGE', value)}/>}
      </SpaceBetweenRow>
      <BodyRegular>{t('settings.signage.subtitle')}</BodyRegular>

      {!!signageEnabled && !isTierRestricted && (
        <>
          <SubSetting>
            <BodyLarge>{t('settings.signage.name-display.title')}</BodyLarge>
            <BodySmall style={{ color: Colours.darkGrey }}>{t('settings.signage.name-display.subtitle')}</BodySmall>
            <SegmentedControl>
              {NAME_DISPLAY_MODES.map(mode => (
                <SegmentButton
                  key={mode}
                  $active={nameDisplay === mode}
                  disabled={isLoading}
                  onClick={() => nameDisplay !== mode && save('SIGNAGE_NAME_DISPLAY', mode)}
                >
                  {t(`settings.signage.name-display.${mode.toLowerCase().replace(/_/g, '-')}`)}
                </SegmentButton>
              ))}
            </SegmentedControl>
          </SubSetting>

          <SubSetting>
            <SpaceBetweenRow>
              <Column>
                <BodyLarge>{t('settings.signage.safety-roles.title')}</BodyLarge>
                <BodySmall style={{ color: Colours.darkGrey }}>{t('settings.signage.safety-roles.subtitle')}</BodySmall>
              </Column>
              <Switch value={showSafetyRoles} label=""
                      disabled={isLoading}
                      onChange={(value: boolean) => save('SIGNAGE_SHOW_SAFETY_ROLES', value)}/>
            </SpaceBetweenRow>
          </SubSetting>
        </>
      )}
    </ConfigurationSection>
  )
}

const SubSetting = styled.div`
  margin-top: 20px;
  margin-left: 24px;
  padding-top: 16px;
  border-top: 1px solid ${Colours.lightGrey};
`;

const SegmentedControl = styled.div`
  display: inline-flex;
  margin-top: 12px;
  border: 1px solid ${Colours.mildGrey};
  border-radius: 8px;
  overflow: hidden;
`;

const SegmentButton = styled.button<{ $active: boolean }>`
  padding: 8px 16px;
  border: none;
  background: ${p => p.$active ? Colours.blue : 'white'};
  color: ${p => p.$active ? 'white' : Colours.black};
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  &:not(:last-child) {
    border-right: 1px solid ${Colours.mildGrey};
  }
`;
